import { useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'

export function useAuth() {
  const { user, session, profile, isLoading, isAdmin, isVenueOrOrganizer } = useAuthStore()

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }, [])

  const signUp = useCallback(
    async (email: string, password: string, fullName: string) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
        },
      })
      if (error) throw error
    },
    []
  )

  // `returnTo` is the deep link the user was headed for when login interrupted
  // — the SMS task link, a shared form, an event tab. Hardcoding `/dashboard`
  // here was the one login door that dropped the destination on the floor;
  // password and magic-link logins both carried it through. Only a same-origin
  // PATH is ever accepted: the value arrives via router state, and anything not
  // shaped like `/…` falls back to the dashboard rather than becoming an open
  // redirect through the OAuth round-trip. The project's auth allow-list is
  // `https://eventripple.app/**` (measured 26.08.2026), so any in-app path is
  // a permitted redirect target.
  const signInWithGoogle = useCallback(async (returnTo?: string) => {
    const path =
      returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/dashboard'
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${path}`,
      },
    })
    if (error) throw error
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw error
  }, [])

  // Secure email change: Supabase emails a confirmation link to BOTH the
  // current and new address (double-confirm is enabled on the project). The
  // email only changes once confirmed; the link lands on /auth/confirm-email.
  // A DB trigger (sync_profile_email) keeps profiles.email in sync afterwards.
  const updateEmail = useCallback(async (newEmail: string) => {
    const { error } = await supabase.auth.updateUser(
      { email: newEmail },
      { emailRedirectTo: `${window.location.origin}/auth/confirm-email` }
    )
    if (error) throw error
  }, [])

  return {
    user,
    session,
    profile,
    isLoading,
    isAdmin,
    isVenueOrOrganizer,
    isAuthenticated: !!session,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    updateEmail,
  }
}
